import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera,
  User,
  Mail,
  Phone,
  Save,
  LogOut,
  Trash2,
  ShieldCheck,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

export default function Profile() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    profile_image: "",
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setProfile({
      name: localStorage.getItem("name") || "",
      email: localStorage.getItem("email") || "",
      phone: localStorage.getItem("phone") || "",
      profile_image: localStorage.getItem("profile_image") || "",
    });
  }, []);

  // Clean temporary image preview
  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  // Select Profile Image
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Optional validation
    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File",
        text: "Please select a valid image file.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "Image Too Large",
        text: "Please choose an image smaller than 5MB.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }

    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  // Save Profile Changes
  const handleSaveChanges = async () => {
    const user_id = localStorage.getItem("user_id");

    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Session Required",
        text: "Please sign in again to update your profile.",
        confirmButtonColor: "#2563EB",
      });

      navigate("/signin");

      return;
    }

    if (!profile.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Name Required",
        text: "Please enter your name.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    const formData = new FormData();

    formData.append("name", profile.name.trim());
    formData.append("phone_number", profile.phone);
    formData.append("user_id", user_id);

    if (selectedFile) {
      formData.append("profile_image", selectedFile);
    }

    setIsSaving(true);

    try {
      const response = await api.post(
        "/update_user_profile/",
        formData
      );

      const data = response.data;

      localStorage.setItem("name", data.name || profile.name);
      localStorage.setItem(
        "phone",
        data.phone || data.phone_number || profile.phone
      );

      if (data.profile_image) {
        localStorage.setItem("profile_image", data.profile_image);
      }

      setProfile((prev) => ({
        ...prev,
        name: data.name || prev.name,
        phone: data.phone || data.phone_number || prev.phone,
        profile_image: data.profile_image || prev.profile_image,
      }));

      setSelectedFile(null);
      setPreviewImage(null);

      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Your profile information has been updated successfully.",
        confirmButtonColor: "#2563EB",
      });
    } catch (error) {
      console.error("Profile update error:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "There was an error updating your profile. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: errorMessage,
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Sign out?",
      text: "You will need to sign in again to access your account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563EB",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, sign out",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      localStorage.clear();
      navigate("/signin");
    }
  };

  // Delete Account
  const handleDeleteAccount = async () => {
    const user_id = localStorage.getItem("user_id");

    const result = await Swal.fire({
      title: "Delete your account?",
      html: `
        <p style="color:#64748b">
          This action is permanent. Your account and associated information
          may no longer be recoverable.
        </p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Delete Account",
      cancelButtonText: "Keep Account",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete("/delete_account/", {
        data: {
          user_id,
        },
      });

      localStorage.clear();

      await Swal.fire({
        icon: "success",
        title: "Account Deleted",
        text: "Your account has been deleted successfully.",
        confirmButtonColor: "#2563EB",
      });

      navigate("/signup");
    } catch (error) {
      console.error("Delete account error:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "We couldn't delete your account. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Deletion Failed",
        text: errorMessage,
        confirmButtonColor: "#2563EB",
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-20 pb-32 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
            <ShieldCheck size={15} />
            Account Settings
          </div>

          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Manage your profile
          </h1>

          <p className="text-slate-300 max-w-2xl text-lg">
            Keep your personal information up to date and manage your Nexindi
            account securely.
          </p>
        </div>
      </section>

      {/* Main Profile Area */}
      <main className="relative -mt-20 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[320px_1fr] gap-7">
            {/* Profile Card */}
            <aside className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden h-fit">
              <div className="h-28 bg-gradient-to-br from-blue-600 to-blue-800" />

              <div className="px-7 pb-8">
                {/* Profile Picture */}
                <div className="relative -mt-14 w-fit mx-auto">
                  <div className="relative">
                    <img
                      src={
                        previewImage ||
                        profile.profile_image ||
                        "/default-avatar.png"
                      }
                      alt={profile.name || "Profile"}
                      className="w-28 h-28 rounded-3xl object-cover border-4 border-white shadow-lg"
                    />

                    <label
                      htmlFor="profile-image"
                      className="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center border-4 border-white cursor-pointer hover:bg-blue-700 transition"
                      title="Change profile picture"
                    >
                      <Camera size={17} />

                      <input
                        id="profile-image"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                </div>

                <div className="text-center mt-5">
                  <h2 className="text-xl font-bold text-slate-900">
                    {profile.name || "Your Profile"}
                  </h2>

                  <p className="text-sm text-slate-500 mt-1 break-all">
                    {profile.email}
                  </p>
                </div>

                <div className="mt-7 rounded-2xl bg-blue-50 border border-blue-100 p-4">
                  <div className="flex gap-3">
                    <ShieldCheck
                      size={20}
                      className="text-blue-600 flex-shrink-0 mt-0.5"
                    />

                    <div>
                      <p className="font-semibold text-slate-900 text-sm">
                        Your account
                      </p>

                      <p className="text-xs text-slate-500 leading-relaxed mt-1">
                        Your personal information is used to manage your
                        account and services.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Settings */}
            <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 p-7 md:p-10">
              <div className="mb-9">
                <p className="text-blue-600 text-sm font-semibold mb-2">
                  Personal Information
                </p>

                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                  Profile details
                </h2>

                <p className="text-slate-500 mt-2">
                  Update your name, phone number, or profile picture.
                </p>
              </div>

              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Full Name
                  </label>

                  <div className="relative">
                    <User
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          name: e.target.value,
                        })
                      }
                      className="w-full h-13 pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address
                  </label>

                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed"
                    />
                  </div>

                  <p className="text-xs text-slate-400 mt-2">
                    Your email address cannot be changed from this page.
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone Number
                  </label>

                  <div className="relative">
                    <Phone
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          phone: e.target.value,
                        })
                      }
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                      placeholder="e.g. 0712345678"
                    />
                  </div>
                </div>
              </div>

              {/* Save */}
              <div className="mt-9 pt-7 border-t border-slate-200 flex justify-end">
                <button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <Save size={17} />

                  {isSaving ? "Saving Changes..." : "Save Changes"}
                </button>
              </div>

              {/* Account Actions */}
              <div className="mt-12">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-slate-900">
                    Account actions
                  </h3>

                  <p className="text-sm text-slate-500 mt-1">
                    Manage your account session or permanently remove your
                    account.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-4 text-left p-5 rounded-2xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
                      <LogOut size={19} />
                    </div>

                    <div>
                      <p className="font-semibold text-slate-900">
                        Sign Out
                      </p>

                      <p className="text-xs text-slate-500 mt-1">
                        Sign out of your current session.
                      </p>
                    </div>
                  </button>

                  {/* Delete */}
                  <button
                    onClick={handleDeleteAccount}
                    className="flex items-center gap-4 text-left p-5 rounded-2xl border border-red-100 hover:border-red-200 hover:bg-red-50 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                      <Trash2 size={19} />
                    </div>

                    <div>
                      <p className="font-semibold text-red-600">
                        Delete Account
                      </p>

                      <p className="text-xs text-slate-500 mt-1">
                        Permanently remove your account.
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}